import axios, { type AxiosError, type AxiosInstance, type AxiosResponse } from 'axios';

import { CSRF_HEADER_NAME } from 'constants/session';
import type { ApiErrorType } from 'models';
import { getSubaccountNumber } from 'stores/zustand/subAccountStore';
import config from 'utils/config';

import { attachTradeGeoComplianceInterceptor } from '../geocomply/tradeRequestInterceptor';
import { attachAwsWafTokenToAxiosConfig, maybeRetryAxiosRequestWithAwsWafToken } from '../security/awsWaf';
import { useAuthStore } from '../stores/authStore';
import { getClickIdHeaders } from './clickIdHeaders';
import { parseError } from './errors/parseError';
import { attachStepUpInterceptor } from './stepUpVerification';
import { createSubaccountInterceptor } from './subaccount';

export interface ApiClientConfig {
    baseURL: string;
    timeout?: number;
    withCredentials?: boolean;
    skipGlobalErrorHandler?: boolean;
}

export const createApiClient = (clientConfig: ApiClientConfig): AxiosInstance => {
    const authActions = useAuthStore.getState().actions;

    const client = axios.create({
        baseURL: clientConfig.baseURL,
        timeout: clientConfig.timeout ?? config.requestTimeoutMs,
        withCredentials: clientConfig.withCredentials ?? true,
        xsrfHeaderName: CSRF_HEADER_NAME,
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
    });

    client.interceptors.request.use(createSubaccountInterceptor(getSubaccountNumber));

    client.interceptors.request.use(async (config) => {
        const csrfToken = authActions.getCsrfToken();
        if (csrfToken) {
            config.headers[CSRF_HEADER_NAME] = csrfToken;
        }
        const clickIdHeaders = getClickIdHeaders(config);
        Object.entries(clickIdHeaders).forEach(([key, value]) => config.headers.set(key, value));
        return attachAwsWafTokenToAxiosConfig(config);
    });

    client.interceptors.response.use((response: AxiosResponse) => {
        const csrfToken = response.headers[CSRF_HEADER_NAME];
        if (csrfToken) {
            authActions.setCsrfToken(csrfToken);
        }
        return response;
    });

    client.interceptors.response.use(
        (response) => response,
        (error: AxiosError) => maybeRetryAxiosRequestWithAwsWafToken(client, error),
    );

    attachStepUpInterceptor(client);
    attachTradeGeoComplianceInterceptor(client);

    if (!clientConfig.skipGlobalErrorHandler) {
        client.interceptors.response.use(
            (response) => response,
            (error: AxiosError<ApiErrorType>) => {
                const parsed = parseError(error);

                if (parsed.unauthorized) {
                    authActions.logout('session_expired');
                }

                return Promise.reject(error);
            },
        );
    }

    return client;
};
